import type { FC, ReactNode } from 'react'
import type { MascotMood } from '@/components/three/mascot-model'

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState
} from 'react'

export interface MascotMessage {
    id: number
    content: ReactNode
}

interface MascotContextValue {
    mood: MascotMood
    moodKey: number
    message: MascotMessage | null
    say: (content: ReactNode, mood?: MascotMood, duration?: number) => void
    react: (mood: MascotMood) => void
    dismiss: () => void
}

const MascotContext = createContext<MascotContextValue | null>(null)

let messageId = 0

export const MascotProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const [mood, setMood] = useState<MascotMood>('idle')
    const [moodKey, setMoodKey] = useState(0)
    const [message, setMessage] = useState<MascotMessage | null>(null)
    const timer = useRef<number | null>(null)

    const dismiss = useCallback(() => {
        if (timer.current) window.clearTimeout(timer.current)

        timer.current = null
        setMessage(null)
    }, [])

    const react = useCallback((nextMood: MascotMood) => {
        setMood(nextMood)
        setMoodKey(key => key + 1)
    }, [])

    const say = useCallback((content: ReactNode, nextMood: MascotMood = 'wave', duration = 6500) => {
        messageId += 1
        setMessage({ id: messageId, content })
        react(nextMood)

        if (timer.current) window.clearTimeout(timer.current)

        timer.current = window.setTimeout(() => {
            setMessage(null)
            timer.current = null
        }, duration)
    }, [react])

    const value = useMemo(() => ({ mood, moodKey, message, say, react, dismiss }), [mood, moodKey, message, say, react, dismiss])

    return (
        <MascotContext.Provider value={value}>
            {children}
        </MascotContext.Provider>
    )
}

export const useMascot = (): MascotContextValue => {
    const context = useContext(MascotContext)

    if (!context) throw new Error('useMascot must be used inside MascotProvider')

    return context
}
